"use client"

import * as React from "react"
import { ChevronLeftIcon, ChevronRightIcon } from "@/components/brand-kit/icon-sprite"

interface PaginationProps {
  currentPage: number
  totalPages: number
  onPageChange: (page: number) => void
  /**
   * Optional per-page href builder. When provided, page controls render as
   * crawlable <a href> anchors (Googlebot follows them to discover deep pages)
   * while still doing client-side navigation via onPageChange on click. When
   * omitted, controls stay <button> (fine for noindex/internal surfaces).
   */
  hrefForPage?: (page: number) => string
  /** Additional classes on the nav element */
  className?: string
}

export function Pagination({ currentPage, totalPages, onPageChange, hrefForPage, className = "" }: PaginationProps) {
  // Build visible page numbers with ellipsis
  const pages: (number | "ellipsis")[] = []
  if (totalPages <= 7) {
    for (let i = 1; i <= totalPages; i++) pages.push(i)
  } else {
    pages.push(1)
    if (currentPage > 3) pages.push("ellipsis")
    const start = Math.max(2, currentPage - 1)
    const end = Math.min(totalPages - 1, currentPage + 1)
    for (let i = start; i <= end; i++) pages.push(i)
    if (currentPage < totalPages - 2) pages.push("ellipsis")
    pages.push(totalPages)
  }

  const arrowCls =
    "w-10 h-10 flex items-center justify-center rounded border border-gray-200 hover:border-[#ff9951] transition-colors text-gray-500 disabled:opacity-40 disabled:cursor-not-allowed"
  const numCls = (active: boolean) =>
    `w-10 h-10 flex items-center justify-center rounded transition-colors ${
      active ? "bg-[#ff9951] text-[var(--color-brand-action-text)] font-black shadow-sm" : "border border-gray-200 hover:bg-gray-50"
    }`

  // Client-nav-on-click without a full navigation, while the href stays real
  // for crawlers, middle-click, and no-JS.
  const onNav = (page: number) => (e: React.MouseEvent) => {
    if (e.metaKey || e.ctrlKey || e.shiftKey || e.button === 1) return
    e.preventDefault()
    onPageChange(page)
  }

  const prevPage = Math.max(1, currentPage - 1)
  const nextPage = Math.min(totalPages, currentPage + 1)

  return (
    // FRIDGE-240: `mb-12` matters as much as `mt-12`. The control previously
    // carried a top margin and NO bottom margin, so wherever a tinted section
    // followed the grid, the pagination sat flush against that panel's edge and
    // read as belonging to the section beneath rather than closing the grid it
    // controls. Fixed on the component, not at one call site, because all five
    // consumers inherit the same asymmetry — brands, blog listing, products,
    // collections (brand-kit variant) and the design reference page.
    // Callers that override the top (`!mt-10` on brand pages) still get a bottom
    // gap at least as large as their top gap, which is the stated requirement.
    <nav className={`flex items-center justify-center gap-2 mt-12 mb-12 ${className}`}>
      {hrefForPage && currentPage > 1 ? (
        <a href={hrefForPage(prevPage)} onClick={onNav(prevPage)} rel="prev" aria-label="Previous page" className={arrowCls}>
          <ChevronLeftIcon className="w-5 h-5" />
        </a>
      ) : (
        <button onClick={() => onPageChange(prevPage)} disabled={currentPage === 1} aria-label="Previous page" className={arrowCls}>
          <ChevronLeftIcon className="w-5 h-5" />
        </button>
      )}

      {pages.map((page, i) =>
        page === "ellipsis" ? (
          <span key={`e${i}`} className="px-2 text-gray-500">
            ...
          </span>
        ) : hrefForPage ? (
          <a
            key={page}
            href={hrefForPage(page)}
            onClick={onNav(page)}
            aria-current={page === currentPage ? "page" : undefined}
            className={numCls(page === currentPage)}
          >
            {page}
          </a>
        ) : (
          <button key={page} onClick={() => onPageChange(page)} className={numCls(page === currentPage)}>
            {page}
          </button>
        )
      )}

      {hrefForPage && currentPage < totalPages ? (
        <a href={hrefForPage(nextPage)} onClick={onNav(nextPage)} rel="next" aria-label="Next page" className={arrowCls}>
          <ChevronRightIcon className="w-5 h-5" />
        </a>
      ) : (
        <button onClick={() => onPageChange(nextPage)} disabled={currentPage === totalPages} aria-label="Next page" className={arrowCls}>
          <ChevronRightIcon className="w-5 h-5" />
        </button>
      )}
    </nav>
  )
}
